import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSettings } from '../context/AppSettingsContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { templateAPI } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, Image } from 'lucide-react';

export const AdminBranding = () => {
    const navigate = useNavigate();
    const { app_name, logo_base64, refresh: refreshSettings } = useAppSettings();
    const [brandingName, setBrandingName] = useState(app_name || '');
    const [brandingLogo, setBrandingLogo] = useState(logo_base64 || '');
    const [saving, setSaving] = useState(false);
    const logoInputRef = useRef(null);

    const handleLogoUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const b64 = ev.target.result.split(',')[1] || '';
            setBrandingLogo(b64);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const current = await templateAPI.get();
            await templateAPI.update({
                ...current.data,
                app_name: brandingName,
                logo_base64: brandingLogo,
            });
            await refreshSettings();
            toast.success('Branding saved!');
        } catch { toast.error('Failed to save branding'); }
        finally { setSaving(false); }
    };

    return (
        <Layout>
            <div className="space-y-4 max-w-2xl mx-auto">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} className="h-10 w-10">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex-1">
                        <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">Branding</h1>
                        <p className="text-slate-500 text-sm">Logo & application name</p>
                    </div>
                </div>

                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
                            <Image className="h-5 w-5 text-red-600" />
                            Company Logo
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {brandingLogo ? (
                            <div className="flex items-center gap-3">
                                <img
                                    src={`data:image/png;base64,${brandingLogo}`}
                                    alt="Logo"
                                    className="h-16 object-contain border border-slate-200 rounded-lg p-2 bg-white"
                                />
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setBrandingLogo('')}
                                    className="h-9 text-xs text-red-600 border-red-200"
                                >
                                    Remove
                                </Button>
                            </div>
                        ) : (
                            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                                <Image className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                                <p className="text-sm text-slate-500">No logo uploaded</p>
                                <p className="text-xs text-slate-400 mt-1">PNG, JPG or SVG recommended</p>
                            </div>
                        )}
                        <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                            data-testid="logo-file-input"
                        />
                        <Button
                            variant="outline"
                            onClick={() => logoInputRef.current?.click()}
                            className="w-full h-11 border-slate-300 text-slate-700"
                            data-testid="upload-logo-btn"
                        >
                            {brandingLogo ? 'Replace Logo' : 'Upload Logo'}
                        </Button>
                    </CardContent>
                </Card>

                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading text-lg uppercase tracking-tight">
                            Application Name
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <Input
                            value={brandingName}
                            onChange={e => setBrandingName(e.target.value)}
                            placeholder="e.g. Flex Plastering Survey"
                            className="h-12 border-slate-300"
                            data-testid="app-name-input"
                        />
                        <p className="text-xs text-slate-400">Shown in the sidebar and browser tab</p>
                    </CardContent>
                </Card>

                <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold uppercase tracking-wide"
                    data-testid="save-branding-btn"
                >
                    {saving
                        ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Saving...</>
                        : <><Save className="mr-2 h-5 w-5" />Save Branding</>}
                </Button>
            </div>
        </Layout>
    );
};
